/**
 * BookDetailModal — shows book details with per-user editing.
 * Current user's progress is always expanded; other users are collapsible.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { DetailModal, formatDate, formatDateShort, CustomSelect } from '@code-burrow/design-system';
import { useUsers, deriveUserLabels, useUserView, useAddToList, usePod, PodMemberPicker, useCurrentUser } from '@code-burrow/tracker-core';

/**
 * CoverPicker — shows thumbnail previews from Google Books and Open Library in a 2-column grid.
 * onSelect receives { source: 'google', zoom } | { source: 'openlibrary', size } | { source: 'openlibrary-id', coverId }.
 * fetchSearchCovers: async () => { covers: [{ coverId, previewUrl }] } — loads OL search results.
 */
function CoverPicker({ googleBooksId, isbn, fetchSearchCovers, onSelect }) {
    const [loading, setLoading] = useState(null);
    const [error, setError] = useState(null);
    const [hidden, setHidden] = useState(new Set());
    const [searchCovers, setSearchCovers] = useState([]);
    const [searching, setSearching] = useState(false);
    const hasFetched = useRef(false);

    // Fetch OL edition covers on mount (once)
    useEffect(() => {
        if (!fetchSearchCovers || hasFetched.current) return;
        hasFetched.current = true;
        setSearching(true);
        fetchSearchCovers()
            .then((result) => setSearchCovers(result?.covers || []))
            .catch(() => {})
            .finally(() => setSearching(false));
    }, [fetchSearchCovers]);

    // Strip hyphens/spaces from ISBN for OL preview URLs
    const cleanIsbn = isbn ? isbn.replace(/[\s-]/g, '') : null;

    const options = [
        ...(googleBooksId ? [
            { key: 'google-2', src: `https://books.google.com/books/content?id=${googleBooksId}&printsec=frontcover&img=1&zoom=2&source=gbs_api`, action: { source: 'google', zoom: 2 } },
            { key: 'google-3', src: `https://books.google.com/books/content?id=${googleBooksId}&printsec=frontcover&img=1&zoom=3&source=gbs_api`, action: { source: 'google', zoom: 3 } },
        ] : []),
        ...(cleanIsbn ? [
            { key: 'ol-M', src: `https://covers.openlibrary.org/b/isbn/${cleanIsbn}-M.jpg?default=false`, action: { source: 'openlibrary', size: 'M' }, hideOnError: true },
            { key: 'ol-L', src: `https://covers.openlibrary.org/b/isbn/${cleanIsbn}-L.jpg?default=false`, action: { source: 'openlibrary', size: 'L' }, hideOnError: true },
        ] : []),
        ...searchCovers.map(({ coverId, previewUrl }) => ({
            key: `ol-id-${coverId}`,
            src: previewUrl,
            action: { source: 'openlibrary-id', coverId },
            hideOnError: true,
        })),
    ].filter(({ key }) => !hidden.has(key));

    const handleSelect = async (key, action) => {
        setLoading(key);
        setError(null);
        try {
            await onSelect(action);
        } catch (err) {
            setError(err.message || 'Failed to update cover');
        } finally {
            setLoading(null);
        }
    };

    const hasOptions = options.length > 0;

    return (
        <div className="px-3 py-2">
            <p className="text-xs text-text-tertiary mb-2">Pick the best cover:</p>
            {error && <p className="text-xs text-red-400 mb-2">{error}</p>}
            {!hasOptions && !searching && (
                <p className="text-xs text-text-tertiary italic">No covers found</p>
            )}
            {hasOptions && (
                <div className="grid grid-cols-2 gap-2 overflow-y-auto max-h-64">
                    {options.map(({ key, src, action, hideOnError }) => (
                        <button
                            key={key}
                            onClick={() => handleSelect(key, action)}
                            disabled={loading !== null}
                            className={[
                                'p-1.5 transition-colors',
                                loading === key ? 'ring-2 ring-accent-primary' : 'hover:bg-surface-hover',
                                loading !== null && loading !== key ? 'opacity-40' : '',
                            ].join(' ')}
                        >
                            <div className="relative w-full aspect-[2/3] overflow-hidden bg-bg-tertiary border border-border-subtle">
                                <img
                                    src={src}
                                    alt="cover option"
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    onError={hideOnError ? () => setHidden((prev) => new Set([...prev, key])) : undefined}
                                />
                                {loading === key && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    </div>
                                )}
                            </div>
                        </button>
                    ))}
                </div>
            )}
            {searching && (
                <div className="flex items-center gap-1.5 mt-2">
                    <div className="w-3 h-3 border border-text-tertiary border-t-transparent rounded-full animate-spin flex-shrink-0" />
                    <span className="text-[10px] text-text-tertiary">Finding more editions…</span>
                </div>
            )}
        </div>
    );
}

const STATUS_OPTIONS = [
    { value: 'Reading',   label: 'Reading',   pillClassName: 'bg-amber-500/15 text-amber-400' },
    { value: 'Read',      label: 'Read',      pillClassName: 'bg-emerald-500/15 text-emerald-300' },
    { value: 'Abandoned', label: 'Abandoned', pillClassName: 'bg-red-500/15 text-red-400' },
    { value: 'On Hold',   label: 'On Hold',   pillClassName: 'bg-orange-500/15 text-orange-400' },
];

const BOOK_USER_FIELDS = [
    { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
    { key: 'rating', label: 'Rating', type: 'rating' },
    { key: 'startDate', label: 'Start Date', type: 'date' },
    { key: 'finishDate', label: 'Finish Date', type: 'date' },
    { key: 'notes', label: 'Notes', type: 'text' },
];

function getUserField(item, userName, fieldName) {
    return item._userData?.[userName]?.[fieldName] ?? null;
}

function dayBefore(dateStr) {
    const d = new Date(dateStr);
    d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString().split('T')[0];
}

/**
 * InlineField -- a single field that shows its value and becomes editable on tap.
 * Toggles and ratings are always interactive (no edit state).
 * Dates, selects, and text enter edit mode on tap, save on blur/change.
 */
function InlineField({ field, value, onSave, disabled }) {
    const [editing, setEditing] = useState(false);
    const [localVal, setLocalVal] = useState('');

    const startEdit = () => {
        if (disabled) return;
        if (field.type === 'date') {
            setLocalVal(value ? new Date(value).toISOString().split('T')[0] : '');
        } else if (field.type === 'select') {
            setLocalVal(value || '');
        } else if (field.type === 'text') {
            setLocalVal(value || '');
        }
        setEditing(true);
    };

    const commitDate = (val) => {
        const parsed = val ? new Date(val).toISOString() : null;
        if (parsed !== (value ? new Date(value).toISOString() : null)) {
            onSave(field.key, parsed);
        }
        setEditing(false);
    };

    const commitSelect = (val) => {
        if (val !== (value || '')) {
            onSave(field.key, val || null);
        }
        setEditing(false);
    };

    const commitText = (val) => {
        if (val !== (value || '')) {
            onSave(field.key, val || null);
        }
        setEditing(false);
    };

    // ── Rating: always interactive, tap stars ──
    if (field.type === 'rating') {
        const rating = value || 0;
        return (
            <div className="flex justify-between items-center min-h-[24px]">
                <span className="text-xs text-text-tertiary">{field.label}</span>
                <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                        <button
                            key={n}
                            type="button"
                            disabled={disabled}
                            onClick={() => onSave(field.key, rating === n ? null : n)}
                            className={`text-base leading-none transition-colors ${
                                n <= rating ? 'text-yellow-400' : 'text-text-tertiary/30 hover:text-text-tertiary/60'
                            } ${disabled ? 'opacity-50' : ''}`}
                        >&#9733;</button>
                    ))}
                </div>
            </div>
        );
    }

    // ── Date: tap to open picker ──
    if (field.type === 'date') {
        if (editing) {
            return (
                <div className="flex justify-between items-center min-h-[24px]">
                    <span className="text-xs text-text-tertiary">{field.label}</span>
                    <div className="flex items-center gap-1">
                        <input
                            type="date"
                            value={localVal}
                            onChange={(e) => setLocalVal(e.target.value)}
                            onBlur={() => commitDate(localVal)}
                            onKeyDown={(e) => { if (e.key === 'Enter') commitDate(localVal); if (e.key === 'Escape') setEditing(false); }}
                            autoFocus
                            className="h-[22px] px-2 rounded-sm bg-bg-primary border border-border text-text-primary text-xs focus:outline-none w-[130px] [color-scheme:dark]"
                        />
                        {localVal && (
                            <button
                                type="button"
                                onClick={() => { setLocalVal(''); commitDate(''); }}
                                className="text-text-tertiary hover:text-text-secondary"
                                title="Clear"
                            >
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
            );
        }
        return (
            <div
                className="flex justify-between items-center min-h-[24px] cursor-pointer rounded -mx-1 px-1 hover:bg-surface-hover transition-colors duration-150"
                onClick={startEdit}
            >
                <span className="text-xs text-text-tertiary">{field.label}</span>
                <span className="text-xs text-text-secondary">{value ? formatDate(value) : '—'}</span>
            </div>
        );
    }

    // ── Select: CustomSelect dropdown, saves immediately on selection ──
    if (field.type === 'select') {
        return (
            <div className="flex justify-between items-center min-h-[24px]">
                <span className="text-xs text-text-tertiary">{field.label}</span>
                <CustomSelect
                    value={value || ''}
                    options={field.options || []}
                    onChange={(val) => onSave(field.key, val || null)}
                    disabled={disabled}
                    placeholder="—"
                    triggerClassName="text-xs text-text-secondary hover:text-text-primary"
                />
            </div>
        );
    }

    // ── Text (notes): tap to edit, save on blur ──
    if (field.type === 'text') {
        if (editing) {
            return (
                <div className="space-y-1">
                    <span className="text-xs text-text-tertiary">{field.label}</span>
                    <textarea
                        value={localVal}
                        onChange={(e) => setLocalVal(e.target.value)}
                        onBlur={() => commitText(localVal)}
                        onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }}
                        autoFocus
                        rows={2}
                        className="w-full bg-bg-tertiary text-text-primary rounded px-1.5 py-1 text-xs border border-border-subtle"
                    />
                </div>
            );
        }
        return (
            <div
                className="cursor-pointer rounded -mx-1 px-1 hover:bg-surface-hover transition-colors duration-150"
                onClick={startEdit}
            >
                <span className="text-xs text-text-tertiary">{field.label}</span>
                <span className="text-xs text-text-secondary block mt-0.5">{value || '—'}</span>
            </div>
        );
    }

    return null;
}

function UserProgressSection({ item, users, labels, onSave, saving, currentUser, podMemberPicker, coSelectedIds = new Set() }) {
    const fields = BOOK_USER_FIELDS;
    const [expandedUsers, setExpandedUsers] = useState(new Set());

    /** Save a single field for a single user */
    const saveField = (userName, fieldKey, value) => {
        onSave(userName, { [fieldKey]: value });
    };

    const meUser = users.find(u => u.name === currentUser?.name);

    // Co-selected users (reading together via PodMemberPicker)
    const coUsers = users.filter(u => u.name !== currentUser?.name && coSelectedIds.has(u.id));

    // Check if a pod member has any activity with this book
    const hasActivity = (userName) => {
        const status = getUserField(item, userName, 'status');
        const rating = getUserField(item, userName, 'rating');
        const finishDate = getUserField(item, userName, 'finishDate');
        const startDate = getUserField(item, userName, 'startDate');
        return !!(status || rating || finishDate || startDate);
    };

    // "From your pods" = others with independent activity who are NOT co-selected
    const othersWithActivity = users.filter(u =>
        u.name !== currentUser?.name && !coSelectedIds.has(u.id) && hasActivity(u.name)
    );

    const toggleExpanded = (userName) => {
        setExpandedUsers((prev) => {
            const next = new Set(prev);
            if (next.has(userName)) next.delete(userName);
            else next.add(userName);
            return next;
        });
    };

    // Build the "You & ..." header
    const togetherLabel = coUsers.length > 0
        ? `You & ${coUsers.map(u => labels[u.name] || u.displayName).join(' & ')}`
        : 'You';
    const togetherEmojis = meUser
        ? [meUser, ...coUsers].map(u => u.emoji).filter(Boolean)
        : [];

    return (
        <div className="space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-text-tertiary">Reading Progress</h3>
            {/* Current user + co-selected together */}
            {meUser && (
                <div className="space-y-2">
                    <div className="flex items-center gap-1.5">
                        <div className="flex -space-x-1.5">
                            {togetherEmojis.map((emoji, i) => (
                                <span key={i} className="text-sm leading-none">{emoji}</span>
                            ))}
                        </div>
                        <span className="text-sm font-medium text-text-primary">{togetherLabel}</span>
                    </div>
                    <div className="space-y-0.5">
                        {fields.map((f) => (
                            <InlineField
                                key={f.key}
                                field={f}
                                value={getUserField(item, meUser.name, f.key)}
                                onSave={(fieldKey, val) => saveField(meUser.name, fieldKey, val)}
                                disabled={saving}
                            />
                        ))}
                        {podMemberPicker}
                    </div>
                </div>
            )}
            {/* Pod members with independent activity (not co-selected) */}
            <h4 className="text-xs font-semibold text-text-tertiary uppercase tracking-wide pt-2">From your pods</h4>
            {othersWithActivity.length === 0 ? (
                <p className="text-xs text-text-tertiary italic">No one else in your pods has read this yet</p>
            ) : (
                othersWithActivity.map((u) => {
                    const isExpanded = expandedUsers.has(u.name);
                    return (
                        <div key={u.name} className="space-y-2">
                            <button
                                onClick={() => toggleExpanded(u.name)}
                                className="flex items-center gap-1.5 w-full text-left cursor-pointer"
                            >
                                <span className="text-sm">{u.emoji}</span>
                                <span className="text-sm font-medium text-text-primary">
                                    {labels[u.name] || u.displayName}
                                </span>
                                {!isExpanded && (() => {
                                    const status = getUserField(item, u.name, 'status');
                                    const rating = getUserField(item, u.name, 'rating');
                                    const parts = [];
                                    if (status) parts.push(status);
                                    if (rating) parts.push('★'.repeat(rating));
                                    return parts.length > 0 ? (
                                        <span className="text-xs text-text-tertiary ml-auto mr-1">{parts.join(' · ')}</span>
                                    ) : null;
                                })()}
                                <svg className={`h-3.5 w-3.5 text-text-tertiary transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            {isExpanded && (
                                <div className="space-y-0.5">
                                    {fields.map((f) => (
                                        <InlineField
                                            key={f.key}
                                            field={f}
                                            value={getUserField(item, u.name, f.key)}
                                            onSave={(fieldKey, val) => saveField(u.name, fieldKey, val)}
                                            disabled={saving}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })
            )}
        </div>
    );
}

export default function BookDetailModal({ item, backendService, pocketbaseURL, onClose, onSaved, onDeleted }) {
    const { users: allUsers } = useUsers();
    const { allowedUsers } = usePod();
    const allowed = allowedUsers('books');
    const users = allowed ? allUsers.filter(u => allowed.includes(u.id)) : allUsers;
    const labels = deriveUserLabels(users);
    const { activeView } = useUserView();
    const currentUser = useCurrentUser();
    const [liveItem, setLiveItem] = useState(item);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [coSelectedIds, setCoSelectedIds] = useState(new Set());
    const [coverPickerOpen, setCoverPickerOpen] = useState(false);
    const [coverPickerStyle, setCoverPickerStyle] = useState({});

    const { addToListAction } = useAddToList({
        backendService,
        activeView: currentUser?.name || activeView,
        getItemData: useCallback(() => ({
            itemId: liveItem.id,
            title: liveItem.title,
            imageURL: liveItem.coverURL || '',
        }), [liveItem]),
    });

    useEffect(() => {
        setLiveItem(item);
    }, [item]);

    const refreshItem = useCallback(async () => {
        try {
            const [bookRecord, userData] = await Promise.all([
                backendService.db.pb.collection('books').getOne(liveItem.id, { $autoCancel: false }),
                backendService.getUserData(currentUser?.id),
            ]);

            const allUserBookRecords = (userData.joinData?.user_books?.records || [])
                .filter((r) => r.book === liveItem.id);
            const pbUsers = userData.users || [];

            // Dedup: prefer most recently saved (updated > created > most complete data).
            const fieldScore = (r) => ['status', 'rating', 'startDate', 'finishDate', 'notes']
                .filter((f) => r[f] !== null && r[f] !== '' && r[f] !== undefined).length;
            const isNewer = (a, b) => {
                if (a.updated && b.updated && a.updated !== b.updated) return a.updated > b.updated;
                if (a.created && b.created && a.created !== b.created) return a.created > b.created;
                return fieldScore(a) > fieldScore(b);
            };
            const latestByUser = new Map();
            for (const rec of allUserBookRecords) {
                const prev = latestByUser.get(rec.user);
                if (!prev || isNewer(rec, prev)) latestByUser.set(rec.user, rec);
            }

            const userDataMap = {};
            for (const [, rec] of latestByUser) {
                const u = pbUsers.find((usr) => usr.id === rec.user);
                if (!u) continue;
                userDataMap[u.name] = {};
                for (const f of ['status', 'rating', 'startDate', 'finishDate', 'notes']) {
                    userDataMap[u.name][f] = rec[f] ?? null;
                }
            }

            const updated = { ...bookRecord, _userData: userDataMap };
            setLiveItem(updated);
            return updated;
        } catch (err) {
            console.error('[refreshItem] failed:', err);
            return null;
        }
    }, [backendService, liveItem.id, currentUser?.id]);

    // Always refresh on open so the modal shows the latest saved data,
    // even if the parent's selectedItem hasn't caught up with a recent save.
    useEffect(() => {
        refreshItem();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Cover picker portal — opened by clicking the cover image
    const coverPickerRef = useRef(null);

    const handleImageClick = useCallback((e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const DROPDOWN_WIDTH = 224;
        // Anchor top-left of the dropdown to the centre of the cover image
        let left = rect.left + rect.width / 2;
        let top = rect.top + rect.height / 2;
        if (left + DROPDOWN_WIDTH > window.innerWidth - 8) left = window.innerWidth - 8 - DROPDOWN_WIDTH;
        if (left < 8) left = 8;
        setCoverPickerStyle({ position: 'fixed', top, left, width: DROPDOWN_WIDTH, zIndex: 9999 });
        setCoverPickerOpen((o) => !o);
    }, []);

    useEffect(() => {
        if (!coverPickerOpen) return;
        const close = () => setCoverPickerOpen(false);
        const handleClick = (e) => {
            if (coverPickerRef.current && !coverPickerRef.current.contains(e.target)) close();
        };
        const handleScroll = (e) => {
            if (coverPickerRef.current && coverPickerRef.current.contains(e.target)) return;
            close();
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [coverPickerOpen]);

    const handleSaveUser = async (userName, data) => {
        setSaving(true);
        setSaveError(null);
        try {
            const adjustedData = { ...data };
            if (data.finishDate && !data.startDate) {
                const currentStart = liveItem._userData?.[userName]?.startDate;
                if (currentStart && currentStart > data.finishDate) {
                    adjustedData.startDate = dayBefore(data.finishDate);
                }
            }
            if (data.finishDate && !data.status) {
                const currentStatus = liveItem._userData?.[userName]?.status;
                if (currentStatus !== 'Read') adjustedData.status = 'Read';
            }
            const savedRecord = await backendService.upsertUserBook(userName, liveItem.id, adjustedData);
            if (savedRecord) {
                const USER_FIELDS = ['status', 'rating', 'startDate', 'finishDate', 'notes'];
                setLiveItem((prev) => ({
                    ...prev,
                    _userData: {
                        ...(prev._userData ?? {}),
                        [userName]: {
                            ...(prev._userData?.[userName] ?? {}),
                            ...Object.fromEntries(
                                Object.keys(adjustedData)
                                    .filter((f) => USER_FIELDS.includes(f))
                                    .map((f) => [f, savedRecord[f] ?? null])
                            ),
                        },
                    },
                }));
            }
            onSaved?.();
        } catch (err) {
            console.error('BookDetailModal save error:', err, { userName, bookId: liveItem.id, data });
            setSaveError(err.message || 'Save failed');
            setTimeout(() => setSaveError(null), 5000);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        // Delete all user_books records first (foreign key constraint)
        try {
            const userBooks = await backendService.pocketbase.collection('user_books').getFullList({
                filter: `book="${liveItem.id}"`,
                $autoCancel: false,
            });
            for (const ub of userBooks) {
                await backendService.proxy.collection('user_books').delete(ub.id);
            }
        } catch { /* no user_books to delete */ }
        await backendService.deleteBook(liveItem.id);
    };

    // Build image URL
    const pbImage = liveItem.coverImage;
    const imageURL = pbImage && pocketbaseURL
        ? `${pocketbaseURL}/api/files/books/${liveItem.id}/${pbImage}`
        : liveItem.coverURL ?? null;

    // Build metadata parts
    const publishYear = liveItem.publishedDate ? liveItem.publishedDate.slice(0, 4) : null;
    const metaParts = [
        liveItem.pageCount && `${liveItem.pageCount} pages`,
        publishYear,
    ].filter(Boolean);

    // Split compound categories like "Fiction / General" into individual tags
    const tags = [...new Set(
        (liveItem.subjects || [])
            .flatMap((s) => s.split(/\s*\/\s*/))
            .map((t) => t.trim())
            .filter(Boolean)
    )].slice(0, 6);

    // Shared cover-change handler used by both the menu and the image-click picker
    const handleCoverSelect = useCallback(async ({ source, zoom, size, coverId }, close) => {
        let result;
        if (source === 'openlibrary') {
            result = await backendService.changeBookCoverFromOL(liveItem.id, liveItem.isbn, size);
        } else if (source === 'openlibrary-id') {
            result = await backendService.changeBookCoverByOLId(liveItem.id, coverId);
        } else {
            result = await backendService.changeBookCover(liveItem.id, zoom);
        }
        if (!result.success) throw new Error(result.error);
        await backendService.clearCatalogCache();
        const updated = await backendService.db.getBook(liveItem.id);
        setLiveItem((prev) => ({ ...prev, ...updated }));
        onSaved?.();
        close();
    }, [backendService, liveItem.id, liveItem.isbn, onSaved]);

    // Portal-based cover picker (opened from the cover image)
    const coverPickerPortal = coverPickerOpen && createPortal(
        <div
            ref={coverPickerRef}
            style={coverPickerStyle}
            className="rounded-lg bg-surface border border-border-subtle shadow-xl py-1 overflow-hidden"
            onMouseDown={(e) => e.stopPropagation()}
        >
            <CoverPicker
                googleBooksId={liveItem.googleBooksId}
                isbn={liveItem.isbn}
                fetchSearchCovers={() => backendService.searchBookCoverOptions(liveItem.title, liveItem.author)}
                onSelect={(action) => handleCoverSelect(action, () => setCoverPickerOpen(false))}
            />
        </div>,
        document.body,
    );

    return (
        <>
        <DetailModal
            item={liveItem}
            onClose={onClose}
            onSaved={onSaved}
            onDeleted={onDeleted}
            onSave={() => {}}
            onDelete={handleDelete}
            fields={[]}
            editable={false}
            menuActions={[
                addToListAction,
                { label: 'Refresh Metadata', onClick: async () => {
                    const result = await backendService.refreshBook(liveItem.id);
                    if (result && !result.success) throw new Error(result.error);
                    const updated = await backendService.db.getBook(liveItem.id);
                    setLiveItem((prev) => ({ ...prev, ...updated }));
                    onSaved?.();
                }},
                {
                    key: 'change-cover',
                    label: 'Change Cover',
                    renderSubmenu: ({ close }) => (
                        <CoverPicker
                            googleBooksId={liveItem.googleBooksId}
                            isbn={liveItem.isbn}
                            fetchSearchCovers={() => backendService.searchBookCoverOptions(liveItem.title, liveItem.author)}
                            onSelect={(action) => handleCoverSelect(action, close)}
                        />
                    ),
                },
            ]}
            header={{
                imageUrl: imageURL,
                imageAlt: liveItem.title,
                imageClass: "w-28 h-auto object-contain",
                onImageClick: handleImageClick,
                title: liveItem.title,
                subtitle: liveItem.author || '',
                tags,
                extra: (
                    <div>
                        {(metaParts.length > 0 || liveItem.isbn) && (
                            <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-text-secondary">
                                {metaParts.map((p, i) => <span key={i}>{p}</span>)}
                                {liveItem.isbn && (
                                    <span className="inline-flex items-center gap-1">
                                        <svg className="w-3.5 h-3.5 text-text-tertiary" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M2 4h2v16H2V4zm4 0h1v16H6V4zm3 0h2v16H9V4zm4 0h1v16h-1V4zm3 0h1v16h-1V4zm3 0h2v16h-2V4z" />
                                        </svg>
                                        {liveItem.isbn}
                                    </span>
                                )}
                            </div>
                        )}
                        {liveItem.goodreadsURL && (
                            <a href={liveItem.goodreadsURL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs text-accent-primary hover:underline">
                                View on Goodreads
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                </svg>
                            </a>
                        )}
                        {liveItem.description && (
                            <p className="mt-3 text-xs text-text-secondary leading-relaxed line-clamp-4">
                                {liveItem.description.replace(/<[^>]*>/g, '')}
                            </p>
                        )}
                    </div>
                ),
            }}
            extraContent={
                <>
                    {saveError && (
                        <div className="mb-3 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20">
                            <p className="text-xs text-red-400">Save failed: {saveError}</p>
                        </div>
                    )}
                    <UserProgressSection
                        item={liveItem}
                        users={users}
                        labels={labels}
                        onSave={handleSaveUser}
                        saving={saving}
                        currentUser={currentUser}
                        coSelectedIds={coSelectedIds}
                        podMemberPicker={
                            <PodMemberPicker
                                itemId={liveItem.id}
                                itemType="book"
                                itemTitle={liveItem.title}
                                activityType="read"
                                label="Read with"
                                onChanged={(selectedIds) => {
                                    setCoSelectedIds(selectedIds);
                                    onSaved?.();
                                }}
                            />
                        }
                    />
                </>
            }
        />
        {coverPickerPortal}
        </>
    );
}
